import {Hotel, Room, RoomOffer} from '@findhotel/sapi'
import {ContentHotel} from '@findhotel/sapi/dist/types/packages/core/src/types/types'

import {safelyGetStorageItem} from '@vio/core/src/_web/utils/persistence/safelyGetStorageItem'
import {safelyRemoveStorageItem} from '@vio/core/src/_web/utils/persistence/safelyRemoveStorageItem'
import {safelySetStorageItem} from '@vio/core/src/_web/utils/persistence/safelySetStorageItem'
import {safeJsonParse} from '@vio/core/src/utils/object'

import {LeadContextRequiredData} from '../../components/DealFreezeButton/type'
import {DealFreezeAnalyticsContext, SearchDetail} from '../../modules/slice'
import {ComponentSource} from '../../types'
import {DF_RESUME_STORAGE_TTL_MS} from './constants'

// localStorage, not sessionStorage: OAuth can complete in a different tab.
export const DF_SELECTED_OFFER_STORAGE_KEY = 'DF_SELECTED_OFFER'

export type SelectedOffer = {
  leadContextRequiredData: LeadContextRequiredData
  offerId: string
  roomOffer?: RoomOffer
  hotel?: Hotel | ContentHotel
  searchDetail: SearchDetail
  room?: Room
  component: ComponentSource | string
  analyticsContext?: DealFreezeAnalyticsContext
}

type StashEnvelope = {
  createdAt: number
  value: SelectedOffer
}

/**
 * Stores the offer a guest selected before sign-in so the post-auth resume
 * can reopen the freeze overlay.
 * @param value - The selected offer payload
 * @param now - Injectable clock for tests
 */
export const writeDealFreezeStash = (
  value: SelectedOffer,
  now: number = Date.now()
): void => {
  safelySetStorageItem(
    'localStorage',
    DF_SELECTED_OFFER_STORAGE_KEY,
    JSON.stringify({createdAt: now, value}),
    'writeDealFreezeStash failed'
  )
}

/**
 * Reads the stashed offer, treating an expired or malformed stash as absent.
 * Falls back to the legacy raw sessionStorage stash written by pre-migration code.
 * @param now - Injectable clock for tests
 * @returns The stashed offer, or null when none is pending
 */
export const readDealFreezeStash = (
  now: number = Date.now()
): SelectedOffer | null => {
  const raw = safelyGetStorageItem(
    'localStorage',
    DF_SELECTED_OFFER_STORAGE_KEY,
    'readDealFreezeStash failed'
  )

  if (raw) {
    const envelope = safeJsonParse<StashEnvelope>(raw)
    if (
      typeof envelope?.createdAt === 'number' &&
      envelope.value?.offerId &&
      now - envelope.createdAt < DF_RESUME_STORAGE_TTL_MS
    ) {
      return envelope.value
    }
    clearDealFreezeStash()
    return null
  }

  // Legacy stashes carry no createdAt, so no TTL check is possible here; the
  // sessionStorage tab lifetime bounds them instead.
  const legacyRaw = safelyGetStorageItem(
    'sessionStorage',
    DF_SELECTED_OFFER_STORAGE_KEY,
    'readDealFreezeStash legacy failed'
  )
  if (legacyRaw) {
    const legacy = safeJsonParse<SelectedOffer>(legacyRaw)
    if (legacy?.offerId) return legacy
  }

  return null
}

/** Clears the pending stash from both current and legacy storage. */
export const clearDealFreezeStash = (): void => {
  safelyRemoveStorageItem(
    'localStorage',
    DF_SELECTED_OFFER_STORAGE_KEY,
    'clearDealFreezeStash failed'
  )
  safelyRemoveStorageItem(
    'sessionStorage',
    DF_SELECTED_OFFER_STORAGE_KEY,
    'clearDealFreezeStash legacy failed'
  )
}
